import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';

/**
 * Multi-site: keeps the active site (authStore.activeSiteId → the X-Site-Id header) in sync with
 * the `?site=<siteId>` URL param. Mounted once inside the Router so deep links and reloads land on
 * the right site. When the active site CHANGES, it drops all cached server data so every query
 * refetches under the new X-Site-Id (the query keys aren't site-aware, so an explicit refetch is
 * what makes a site switch show the right content). Renders nothing.
 */
export function ActiveSiteSync(): null {
  const [params] = useSearchParams();
  const queryClient = useQueryClient();
  const urlSite = params.get('site');
  const prev = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const next = urlSite || null;
    const current = useAuthStore.getState().activeSiteId;

    if (urlSite) {
      // The URL explicitly names a site → make it the active one.
      if (next !== current) useAuthStore.getState().setActiveSiteId(next);
      // Whenever the studio (re)mounts at a site OR the site changes, DROP the stale site-scoped
      // cache. The site-config / listings / navigation query keys aren't site-aware, so a still-
      // fresh (5-min staleTime) entry from a PREVIOUSLY opened site — usually the tenant's primary
      // — was being served for a different site, and the studio rendered the wrong site's content
      // (and its subdomain). removeQueries forces a clean refetch under the new X-Site-Id;
      // invalidateQueries refreshes the rest. (Reading via the X-Site-Id header alone only worked
      // when the cache happened to be empty, e.g. right after a hard reload — hence the bug.)
      if (next !== prev.current) {
        queryClient.removeQueries({ queryKey: ['site-config'] });
        queryClient.invalidateQueries();
        prev.current = next;
      }
    } else {
      // No ?site in the URL (e.g. the "My Website" nav → /studio): PRESERVE the site you were
      // editing instead of snapping back to the primary. Only an explicit ?site (deep link,
      // "Edit in Studio", or the SiteSwitcher) changes the active site.
      prev.current = current;
    }
  }, [urlSite, queryClient]);

  return null;
}

export default ActiveSiteSync;
